import { Suspense, lazy, useEffect } from 'react';
import 'react-quill/dist/quill.snow.css';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from './store';
import { resetUIVisibility } from './utils/uiUtils';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import RootLayout from './layouts/RootLayout';
import AuthWrapper from './components/AuthWrapper';
import LayoutManager from './components/LayoutManager';
import RouteChangeListener from './components/RouteChangeListener';

// Lazy load components
const Home = lazy(() => import('./pages/Home'));
const Register = lazy(() => import('./pages/Register'));
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Profile = lazy(() => import('./pages/Profile'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));
const MessageInbox = lazy(() => import('./components/MessageInbox'));
const CalendarPage = lazy(() => import('./pages/calendar/CalendarPage'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ThumbnailDebugger = lazy(() => import('./components/ThumbnailDebugger'));

// Admin pages
const AdminOverview = lazy(() => import('./pages/admin/AdminOverview'));
const UserManagement = lazy(() => import('./pages/admin/UserManagement'));
const AddUser = lazy(() => import('./pages/admin/AddUser'));
const EditUser = lazy(() => import('./pages/admin/EditUser'));
const Categories = lazy(() => import('./pages/admin/Categories'));
const AddCategory = lazy(() => import('./pages/admin/AddCategory'));
const Groups = lazy(() => import('./pages/admin/Groups'));
const AddGroup = lazy(() => import('./pages/admin/AddGroup'));
const EventsEngine = lazy(() => import('./pages/admin/EventsEngine'));
const Settings = lazy(() => import('./pages/admin/Settings'));
const ImportExport = lazy(() => import('./pages/admin/ImportExport'));
const OrganizationsList = lazy(() => import('./pages/admin/organizations/OrganizationsList'));
const Reports = lazy(() => import('./pages/admin/Reports'));

// Course pages
const CourseManagement = lazy(() => import('./pages/admin/CourseManagement'));
const CourseCreate = lazy(() => import('./pages/admin/CourseCreate'));
const CourseDetail = lazy(() => import('./pages/admin/CourseDetail'));
const CourseList = lazy(() => import('./pages/admin/CourseList'));
const CourseLauncher = lazy(() => import('./pages/CourseLauncher'));

// Skip React Router for specific paths
const shouldSkipRouting = () => {
  const path = window.location.pathname;
  
  if (path.startsWith('/uploads/scorm/') || 
      path.startsWith('/api/uploads/scorm/') ||
      path.startsWith('/scorm/s3/') ||
      path.includes('/index_lms.html') ||
      path.includes('/index.html')) {
    return true;
  }
  
  return false;
};

const LoadingFallback = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const App = () => {
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetUIVisibility());
  }, [dispatch]);

  if (shouldSkipRouting()) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PersistGate loading={<LoadingFallback />} persistor={persistor}>
        <Router>
          <RouteChangeListener />
          <ErrorBoundary>
            <AuthWrapper>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
                  <Route element={<LayoutManager><Outlet /></LayoutManager>}>
                    <Route element={<RootLayout />}>
                      {/* Public Routes */}
                      <Route
                        index
                        element={user ? <Navigate to="/dashboard" replace /> : <Home />}
                      />
                      <Route 
                        path="register" 
                        element={user ? <Navigate to="/dashboard" replace /> : <Register />} 
                      />
                      <Route 
                        path="login" 
                        element={user ? <Navigate to="/dashboard" replace /> : <Login />} 
                      />
                      <Route 
                        path="signup" 
                        element={user ? <Navigate to="/dashboard" replace /> : <Signup />} 
                      />
                      <Route path="search" element={<SearchResults />} />
                      <Route path="unauthorized" element={<Unauthorized />} />
                      <Route 
                        path="forgot-password" 
                        element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} 
                      />
                      <Route 
                        path="reset-password/:token" 
                        element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} 
                      />

                      {/* Protected Routes */}
                      {user && (
                        <>
                          <Route
                            path="dashboard"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <Dashboard />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="profile"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin', 'superadmin']}>
                                <Profile />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="messages"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <MessageInbox />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="calendar"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <CalendarPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="courses/:courseId/learn"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <CourseLauncher />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="courses/:courseId"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <CourseLauncher />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="courses/:courseId/modules/:moduleId/lessons/:lessonId"
                            element={
                              <PrivateRoute roles={['admin', 'instructor', 'learner', 'org_admin']}>
                                <CourseLauncher />
                              </PrivateRoute>
                            }
                          />
                        </>
                      )}

                      {/* Admin Routes */}
                      <Route
                        path="admin/*"
                        element={
                          <PrivateRoute roles={['admin', 'instructor', 'org_admin', 'superadmin']}>
                            <Outlet />
                          </PrivateRoute>
                        }
                      >
                        <Route index element={<AdminOverview />} />
                        <Route path="courses" element={<CourseManagement />} />
                        <Route path="courses/create" element={<CourseCreate />} />
                        <Route path="courses/:courseId" element={<CourseDetail />} />
                        <Route path="groups" element={<Groups />} />
                        <Route path="groups/add" element={<AddGroup />} />
                        <Route path="reports" element={<Reports />} />
                        <Route path="events/*" element={<EventsEngine />} />
                        <Route path="import-export/*" element={<ImportExport />} />
                        <Route path="settings/*" element={<Settings />} />
                        <Route path="thumbnail-debug" element={<ThumbnailDebugger />} />

                        {(user?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'superadmin') && (
                          <>
                            <Route path="overview" element={<AdminOverview />} />
                            <Route path="users" element={<UserManagement />} />
                            <Route path="users/add" element={<AddUser />} />
                            <Route path="users/edit/:userId" element={<EditUser />} />
                            <Route path="categories" element={<Categories />} />
                            <Route path="categories/add" element={<AddCategory />} />
                          </>
                        )}
                      </Route>

                      {/* Fallback Routes */}
                      <Route
                        path="*"
                        element={
                          <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                            <Typography variant="h5">Page Not Found</Typography>
                          </Box>
                        }
                      />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </AuthWrapper>
          </ErrorBoundary>
        </Router>
      </PersistGate>
    </LocalizationProvider>
  );
};

export default App;